import { useEffect, useRef, useState } from 'react';
import type { Message } from '../types';

/** True only when the browser positively reports no connection (SSR/unknown => online). */
export function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Tracks `navigator.onLine` through the window `online`/`offline` events. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => !isBrowserOffline());
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = (event: Event) => setOnline(event.type === 'online');
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    // Catch a change that happened between the initial render and this effect.
    setOnline(!isBrowserOffline());
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

/**
 * When the connection comes back, resends (once each, oldest first) the failed
 * messages that were queued because the browser was offline. Failures for any
 * other reason (server errors, timeouts) keep their manual Retry.
 */
export function useRetryOnReconnect(
  online: boolean,
  messages: Message[],
  retry: (messageId: string) => Promise<unknown>
): void {
  const wasOnline = useRef(online);
  const latest = useRef({ messages, retry });
  latest.current = { messages, retry };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const reconnected = online && !wasOnline.current;
    wasOnline.current = online;
    if (!reconnected) return;
    const ids = latest.current.messages
      .filter(m => m.status === 'failed' && m.retryOnReconnect)
      .map(m => m.id);
    void (async () => {
      // Sequential: a new send supersedes (aborts) one still in flight.
      for (const id of ids) {
        if (!mounted.current || isBrowserOffline()) return;
        await latest.current.retry(id);
      }
    })();
  }, [online]);
}
